import type { TeamStatement } from '../../store/api/teamsApi';

/**
 * Agent access presets shown in the team drawer. Each maps to IAM-style
 * statements on `agent/<id>`; `custom` marks statements the presets do not
 * describe, which are kept untouched on save.
 */
export type AgentAccessLevel = 'none' | 'use' | 'full' | 'deny' | 'custom';

export const AGENT_ACCESS_OPTIONS: { value: Exclude<AgentAccessLevel, 'custom'>; label: string }[] = [
  { value: 'none', label: 'Not set' },
  { value: 'use', label: 'Use (open and chat)' },
  { value: 'full', label: 'Full (use and edit)' },
  { value: 'deny', label: 'Deny' },
];

export const AGENT_ACCESS_LABELS: Record<AgentAccessLevel, string> = {
  none: 'Not set',
  use: 'Use',
  full: 'Full',
  deny: 'Deny',
  custom: 'Custom',
};

export function agentResource(agentId: string): string {
  return `agent/${agentId}`;
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((item) => set.has(item));
}

/** Read a team's access level for one agent from its statements. */
export function agentAccessLevel(statements: TeamStatement[], agentId: string): AgentAccessLevel {
  const resource = agentResource(agentId);
  const mine = statements.filter((s) => s.resource === resource);
  if (mine.length === 0) return 'none';
  const denies = mine.filter((s) => s.effect === 'deny').map((s) => s.action);
  const allows = mine.filter((s) => s.effect === 'allow').map((s) => s.action);
  if (denies.length > 0) {
    return allows.length === 0 && sameSet(denies, ['agents:*']) ? 'deny' : 'custom';
  }
  if (sameSet(allows, ['agents:*'])) return 'full';
  if (sameSet(allows, ['agents:read', 'agents:invoke'])) return 'use';
  return 'custom';
}

function statementsFor(level: AgentAccessLevel, agentId: string): TeamStatement[] {
  const resource = agentResource(agentId);
  switch (level) {
    case 'use':
      return [
        { effect: 'allow', action: 'agents:read', resource },
        { effect: 'allow', action: 'agents:invoke', resource },
      ];
    case 'full':
      return [{ effect: 'allow', action: 'agents:*', resource }];
    case 'deny':
      return [{ effect: 'deny', action: 'agents:*', resource }];
    default:
      return [];
  }
}

/** Replace one agent's statements with a preset; other statements stay. */
export function withAgentAccess(
  statements: TeamStatement[],
  agentId: string,
  level: AgentAccessLevel,
): TeamStatement[] {
  if (level === 'custom') return statements;
  const resource = agentResource(agentId);
  return [...statements.filter((s) => s.resource !== resource), ...statementsFor(level, agentId)];
}

/* ---------- Team roles: the same presets, written as permission sets ---------- */

export const AGENT_USER_SET = 'agent_user_permission_set';
export const AGENT_BUILDER_SET = 'agent_builder_permission_set';

/** Pass as the agent id for a grant on every organisation agent (`agent/*`). */
export const ALL_AGENTS = '*';

/** Agent permission sets an org role can deny ("Not allowed"); otherwise teams decide. */
export const AGENT_PERMISSION_SETS = [
  { id: AGENT_USER_SET, label: 'Use agents', description: 'Open and chat with agents' },
  {
    id: AGENT_BUILDER_SET,
    label: 'Build agents',
    description: 'Configure, publish and delete agents',
  },
] as const;

function roleGrantsFor(level: AgentAccessLevel, agentId: string): TeamStatement[] {
  const resource = agentResource(agentId);
  switch (level) {
    case 'use':
      return [{ effect: 'allow', action: AGENT_USER_SET, resource }];
    case 'full':
      return [
        { effect: 'allow', action: AGENT_USER_SET, resource },
        { effect: 'allow', action: AGENT_BUILDER_SET, resource },
      ];
    case 'deny':
      return [
        { effect: 'deny', action: AGENT_USER_SET, resource },
        { effect: 'deny', action: AGENT_BUILDER_SET, resource },
      ];
    default:
      return [];
  }
}

/** Read a team role's access level for one agent (or `ALL_AGENTS`). */
export function roleAccessLevel(grants: TeamStatement[], agentId: string): AgentAccessLevel {
  const resource = agentResource(agentId);
  const mine = grants.filter((g) => g.resource === resource);
  if (mine.length === 0) return 'none';
  const denies = mine.filter((g) => g.effect === 'deny').map((g) => g.action);
  const allows = mine.filter((g) => g.effect === 'allow').map((g) => g.action);
  const both = [AGENT_USER_SET, AGENT_BUILDER_SET];
  if (denies.length > 0) {
    return allows.length === 0 && sameSet(denies, both) ? 'deny' : 'custom';
  }
  if (sameSet(allows, both)) return 'full';
  if (sameSet(allows, [AGENT_USER_SET])) return 'use';
  return 'custom';
}

/** Replace one agent's grants on a team role with a preset; other grants stay. */
export function withRoleAccess(
  grants: TeamStatement[],
  agentId: string,
  level: AgentAccessLevel,
): TeamStatement[] {
  if (level === 'custom') return grants;
  const resource = agentResource(agentId);
  return [...grants.filter((g) => g.resource !== resource), ...roleGrantsFor(level, agentId)];
}

/** Short summary of a role, e.g. "Use all agents · Deny 1 agent". */
export function describeRoleGrants(grants: TeamStatement[]): string {
  const levels = new Map<string, AgentAccessLevel>();
  grants.forEach((g) => {
    const agentId = g.resource.startsWith('agent/') ? g.resource.slice('agent/'.length) : g.resource;
    if (!levels.has(agentId)) levels.set(agentId, roleAccessLevel(grants, agentId));
  });
  if (levels.size === 0) return 'No agent access yet';
  const parts: string[] = [];
  const all = levels.get(ALL_AGENTS);
  if (all && all !== 'none') parts.push(`${AGENT_ACCESS_LABELS[all]} all agents`);
  (['use', 'full', 'deny', 'custom'] as AgentAccessLevel[]).forEach((level) => {
    const count = [...levels.entries()].filter(
      ([agentId, value]) => agentId !== ALL_AGENTS && value === level,
    ).length;
    if (count > 0) parts.push(`${AGENT_ACCESS_LABELS[level]} ${count} agent${count === 1 ? '' : 's'}`);
  });
  return parts.join(' · ');
}
